<template>
  <PurpleHexContainer
    class="splash-hero flex flex-wrap items-center justify-center"
    :class="banner ? 'has-banner' : ''"
  >
    <HomeNav
      class="top-nav left-0 right-0 top-0 z-40 custom-fixed"
      :white-logo="whiteLogo"
      :white-logo-size="whiteLogoSize"
      :company-name="companyName"
      :promotion="promotion"
    />
    <div
      class="flex flex-col items-center mb-4 lg:mb-0 px-6 lg:mr-14 xl:mr-24 text-white"
    >
      <div class="font-bold">
        <TypeDisplay
          variant="peta"
          weight="strong"
          class="hero-title max-w-lg text-center"
          data-qa="hero-title"
        >
          <h1 class="mb-2" v-html="title"></h1>
        </TypeDisplay>
        <TypeHeading
          variant="headline"
          weight="strong"
          class="hero-subtitle text-center"
          data-qa="hero-subtitle"
        >
          <p class="mb-4" v-html="subtitle"></p>
        </TypeHeading>
      </div>
      <ul>
        <li
          v-for="item in list"
          :key="item"
          class="font-body-text font-medium mb-2 flex items-baseline"
        >
          <BaseIcon icon="check" class="h-3.5 w-3.5 mr-3 text-secondary" />
          {{ item }}
        </li>
      </ul>
    </div>
    <RegistrationPrompt button-id="company-size-button-hero" />
    <picture class="w-full mt-8 xs:mt-12 sm:mt-16">
      <source
        srcset="
          ~/assets/images/splash/hero-xl.webp,
          ~/assets/images/splash/hero-xl-2x.webp 2x
        "
        media="(min-width: 1280px)"
        type="image/webp"
      />
      <source
        srcset="
          ~/assets/images/splash/hero-lg.webp,
          ~/assets/images/splash/hero-lg-2x.webp 2x
        "
        media="(min-width: 1024px)"
        type="image/webp"
      />
      <source
        srcset="
          ~/assets/images/splash/hero-md.webp,
          ~/assets/images/splash/hero-md-2x.webp 2x
        "
        media="(min-width: 768px)"
        type="image/webp"
      />
      <source
        srcset="
          ~/assets/images/splash/hero-sm.webp,
          ~/assets/images/splash/hero-sm-2x.webp 2x
        "
        media="(min-width: 640px)"
        type="image/webp"
      />
      <source
        srcset="
          ~/assets/images/splash/hero-xs.webp,
          ~/assets/images/splash/hero-xs-2x.webp 2x
        "
        media="(min-width: 480px)"
        type="image/webp"
      />
      <source
        srcset="
          ~/assets/images/splash/hero-2xs.webp,
          ~/assets/images/splash/hero-2xs-2x.webp 2x
        "
        media="(max-width: 479px)"
        type="image/webp"
      />
      <source
        srcset="~/assets/images/splash/hero-xl.png"
        media="(min-width: 1280px)"
        type="image/png"
      />
      <source
        srcset="~/assets/images/splash/hero-lg.png"
        media="(min-width: 1024px)"
        type="image/png"
      />
      <source
        srcset="~/assets/images/splash/hero-md.png"
        media="(min-width: 768px)"
        type="image/png"
      />
      <source
        srcset="~/assets/images/splash/hero-sm.png"
        media="(min-width: 640px)"
        type="image/png"
      />
      <source
        srcset="~/assets/images/splash/hero-xs.png"
        media="(min-width: 480px)"
        type="image/png"
      />
      <img
        src="~/assets/images/splash/hero-2xs.png"
        class="block m-auto max-w-full w-auto h-auto"
        alt=""
        height="265"
        width="375"
      />
    </picture>
  </PurpleHexContainer>
</template>

<script>
import { BaseIcon, TypeDisplay, TypeHeading } from '@bambeehr/pollen';
import { computed, useRoute } from '@nuxtjs/composition-api';
import HomeNav from '../../HomePage/components/Navigation/HomeNav.vue';

import Benefits from '@/constants/Benefits';
import PurpleHexContainer from '@/components/PurpleHexContainer';
import RegistrationPrompt from '@/components/RegistrationPrompt';
import useFreeTrialPromo from '@/hooks/useFreeTrialPromo';
import Campaign, {
  promotionType,
} from '@/modules/SplashPage/constants/Campaign';
import usePartnerPromo from '@/modules/SplashPage/hooks/usePartnerPromo';

export default {
  name: 'SplashHero',
  components: {
    BaseIcon,
    HomeNav,
    PurpleHexContainer,
    RegistrationPrompt,
    TypeDisplay,
    TypeHeading,
  },
  props: {
    hideSubtitle: {
      type: Boolean,
      default: false,
    },
    list: {
      type: Array,
      default: () => [...Benefits],
    },
    title: {
      type: String,
      default: 'Hire an HR Manager',
    },
    whiteLogo: {
      type: String,
      default: '',
    },
    whiteLogoSize: {
      type: String,
      default: '',
    },
    companyName: {
      type: String,
      default: '',
    },
    promotion: {
      type: String,
      default: promotionType.SETUP_FEE_DISCOUNT,
    },
  },
  setup() {
    const { isAPartnerPath } = usePartnerPromo();
    const { isFreeTrialPage } = useFreeTrialPromo();
    const route = useRoute();
    const subtitle = computed(() => {
      if (isFreeTrialPage.value) {
        return 'All for $99/month. <u>Try for Free Today.</u>';
      }

      return 'All for $99/month';
    });
    const banner = computed(() => {
      if (route.value.name === Campaign.FREE_TRIAL) {
        return true;
      }
      if (
        route.value.query.utm_medium === 'sem-br' ||
        route.value.name === Campaign.RADIO
      ) {
        return true;
      }

      return isAPartnerPath.value;
    });

    return {
      isFreeTrialPage,
      subtitle,
      banner,
    };
  },
  methods: {
    handleSignIn() {
      window.location.href = process.env.APP;
    },
    handleTryBambeeNow() {
      this.$router.push('/registration/company-size');
    },
  },
};
</script>

<style scoped>
.splash-hero {
  padding-top: 120px;
}

.splash-hero.has-banner {
  padding-top: 170px;
}

.custom-fixed {
  position: fixed !important;
}

@media screen and (max-width: 768px) {
  .splash-hero {
    padding-top: 160px;
  }

  .splash-hero.has-banner {
    padding-top: 240px;
  }
}
</style>
